// ─── GBCOSPACK — Quotation Document Formatters ───────────────────────────────
// locale-aware formatting helpers for numbers, dates, and money.
// Uses Intl APIs — no external deps.

import type { DocumentLocale } from './types';

// --------------------------------------------------------------------------
// Currency decimal places (mirrors money.ts)
// --------------------------------------------------------------------------
const CURRENCY_DECIMALS: Record<string, number> = {
  USD: 2, EUR: 2, GBP: 2, CNY: 2,
  JPY: 0, KRW: 0, SGD: 2, AED: 2,
  BRL: 2, RUB: 2, INR: 2, VND: 0,
  TRY: 2, THB: 2, HKD: 2, CAD: 2,
  AUD: 2, CHF: 2, MYR: 2, IDR: 0,
  PHP: 2, TWD: 2, NZD: 2, ZAR: 2,
};

function getCurrencyDecimals(code: string): number {
  return CURRENCY_DECIMALS[(code || 'USD').toUpperCase()] ?? 2;
}

// --------------------------------------------------------------------------
// Map our DocumentLocale to BCP 47 locale tags
// --------------------------------------------------------------------------
const LOCALE_MAP: Record<DocumentLocale, string> = {
  en: 'en-US',
  ko: 'ko-KR',
  'zh-CN': 'zh-CN',
};

function toBcp47(locale: DocumentLocale): string {
  return LOCALE_MAP[locale] ?? 'en-US';
}

// --------------------------------------------------------------------------
// Number formatting
// --------------------------------------------------------------------------

/** Format an integer quantity (no decimals, with grouping) */
export function formatQuantity(value: number, locale: DocumentLocale): string {
  try {
    return new Intl.NumberFormat(toBcp47(locale), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return String(value);
  }
}

/** Format a currency amount.  Returns e.g. "USD 3,350.00" */
export function formatMoney(
  value: number | null | undefined,
  currency: string,
  locale: DocumentLocale,
): string {
  if (value == null) return '-';
  const code = (currency || 'USD').toUpperCase();
  const dp = getCurrencyDecimals(code);
  try {
    const formatted = new Intl.NumberFormat(toBcp47(locale), {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(value);
    return `${code} ${formatted}`;
  } catch {
    return `${code} ${value.toFixed(dp)}`;
  }
}

/** Format a unit price preserving up to 6 decimal places */
export function formatUnitPrice(
  value: number | null | undefined,
  currency: string,
  locale: DocumentLocale,
): string {
  if (value == null) return '-';
  const code = (currency || 'USD').toUpperCase();
  const dp = Math.max(getCurrencyDecimals(code), 2);
  try {
    const formatted = new Intl.NumberFormat(toBcp47(locale), {
      minimumFractionDigits: dp,
      maximumFractionDigits: 6,
    }).format(value);
    return `${code} ${formatted}`;
  } catch {
    return `${code} ${value.toFixed(dp)}`;
  }
}

// --------------------------------------------------------------------------
// Date formatting
// --------------------------------------------------------------------------

/**
 * Format an ISO date string (or any parseable date string) per document locale.
 *
 * en  → Aug 27, 2026
 * ko  → 2026년 8월 27일
 * zh-CN → 2026年8月27日
 */
export function formatDate(
  isoDate: string | null | undefined,
  locale: DocumentLocale,
): string {
  if (!isoDate) return '';
  // Handle "To be confirmed" or other non-date strings gracefully
  const parsed = new Date(isoDate);
  if (isNaN(parsed.getTime())) return isoDate;

  try {
    return new Intl.DateTimeFormat(toBcp47(locale), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(parsed);
  } catch {
    return isoDate;
  }
}

/**
 * Format an ISO date string in business-document short style.
 *
 * en  → Aug 27, 2026
 * ko  → 2026. 8. 27.
 * zh-CN → 2026年8月27日
 */
export function formatDateShort(
  isoDate: string | null | undefined,
  locale: DocumentLocale,
): string {
  if (!isoDate) return '';
  const parsed = new Date(isoDate);
  if (isNaN(parsed.getTime())) return isoDate;

  if (locale === 'en') {
    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(parsed);
    } catch {
      return isoDate;
    }
  }
  return formatDate(isoDate, locale);
}